import classNames from "classnames";
import React, { FC, useState } from "react";
import { LuUserPlus } from "react-icons/lu";
import { toast } from "react-toastify";

import {
  useGetClientPortalQuery,
  useRemovePortalAccessMutation,
  useResendPortalInviteMutation,
} from "../../slices/ClientPortalSlice";
import { Avatar } from "../portal/components/PortalUi";
import { ClientPortalPerson } from "./clientPortal.types";
import {
  personDetail,
  personName,
  portalCardPeople,
  portalEmptyText,
  possessive,
  statusLabel,
} from "./clientPortal.utils";
import { PortalEmptyState } from "./PortalEmptyState";
import { errorText, modalCancel, PortalModal } from "./PortalModal";

const pill: Record<ClientPortalPerson["status"], string> = {
  active: "bg-[#E9F9F1] text-[#147A4B]",
  invited: "bg-[#FFF4DB] text-[#8A5A00]",
  revoked: "bg-[#F1F2F5] text-[#6B7185]",
  none: "bg-[#F1F2F5] text-[#6B7185]",
};

/**
 * "People" tab of the client profile's Client portal widget: who can sign in to
 * this client's portal. Inviting is the "Invite to portal" button in the Client
 * portal tab's header (owner 2026-10-28: visible on every inner tab).
 */
export const PortalPeopleSection: FC<{
  customerId: string;
  /** Opens the tab header's invite window (owner's design: Invite in the empty box too). */
  onInvite?: () => void;
}> = ({ customerId, onInvite }) => {
  const { data, isLoading, error } = useGetClientPortalQuery(
    { customerId },
    { skip: !customerId },
  );
  const [resend, resendState] = useResendPortalInviteMutation();
  const [remove, removeState] = useRemovePortalAccessMutation();
  const [removing, setRemoving] = useState<ClientPortalPerson | null>(null);

  const people = portalCardPeople(data?.people ?? []);
  const archived = !!data?.archived;

  const onResend = async (p: ClientPortalPerson) => {
    if (!p.accessId) return;
    const result = await resend(p.accessId);
    if ("error" in result)
      return toast(errorText(result.error), { type: "error" });
    if (result.data.failed.length)
      return toast("The invitation email couldn't be sent. Please try again.", {
        type: "error",
      });
    toast(`Invitation sent again to ${p.email}`, { type: "success" });
  };

  const onRemove = async () => {
    if (!removing?.accessId) return;
    const result = await remove(removing.accessId);
    if ("error" in result) toast(errorText(result.error), { type: "error" });
    else
      toast(`${personName(removing)} no longer has portal access`, {
        type: "success",
      });
    setRemoving(null);
  };

  return (
    <>
      <div className={"flex flex-col gap-3"}>
        <div className={"flex items-center gap-3"}>
          <span
            className={
              "min-w-0 flex-1 text-[12.5px] font-medium text-[#6B7185]"
            }>
            Contacts who can sign in, upload files and see what you share
          </span>
        </div>

        {isLoading ? (
          <span className={"text-[13px] text-[#6B7185]"}>Loading…</span>
        ) : error ? (
          <span className={"text-[13px] font-semibold text-red-500"}>
            {errorText(error, "The client portal couldn't be loaded.")}
          </span>
        ) : people.length ? (
          <div className={"flex flex-col gap-2"}>
            {people.map((p) => {
              const name = personName(p);
              return (
                <div
                  key={p.email}
                  className={
                    "group flex items-center gap-2.5 rounded-[10px] border border-[#EDEFF3] px-3 py-2.5"
                  }>
                  <Avatar name={name} size={32} />
                  <div className={"flex min-w-0 flex-1 flex-col"}>
                    <span
                      className={
                        "truncate text-[13.5px] font-bold text-[#1F2433]"
                      }
                      title={p.email}>
                      {name}
                    </span>
                    <span
                      className={"truncate text-xs font-medium text-[#6B7185]"}>
                      {personDetail(p)}
                      {p.status === "invited" && (
                        <>
                          {" · "}
                          <button
                            type={"button"}
                            className={
                              "font-bold text-[#6B4EFF] hover:underline"
                            }
                            disabled={resendState.isLoading}
                            onClick={() => onResend(p)}>
                            Resend
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                  <span
                    className={classNames(
                      "whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold",
                      pill[p.status],
                    )}>
                    {statusLabel[p.status]}
                  </span>
                  {p.status !== "revoked" && (
                    <button
                      type={"button"}
                      aria-label={`Remove ${name}'s access`}
                      title={"Remove access"}
                      onClick={() => setRemoving(p)}
                      className={
                        "rounded-md px-1.5 text-[#9097A8] opacity-0 hover:bg-[#F1F2F5] hover:text-[#B42318] focus:opacity-100 group-hover:opacity-100"
                      }>
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <PortalEmptyState
            testId={"portal-people-empty"}
            icon={<LuUserPlus size={22} />}
            title={
              archived ? "This client is archived" : "Nobody has access yet"
            }
            text={
              archived
                ? portalEmptyText(true)
                : "Invite this client’s contacts so they can upload documents and see what you share."
            }
            actionLabel={archived ? undefined : "Invite"}
            onAction={onInvite}
          />
        )}
      </div>

      {removing && (
        <PortalModal
          title={"Remove portal access?"}
          onClose={() => setRemoving(null)}
          width={440}
          footer={
            <>
              <button
                type={"button"}
                className={modalCancel}
                onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button
                type={"button"}
                onClick={onRemove}
                disabled={removeState.isLoading}
                className={
                  "h-10 rounded-[9px] bg-[#D92D20] px-5 text-[13.5px] font-extrabold text-white hover:bg-[#B42318] disabled:opacity-60"
                }>
                {removeState.isLoading ? "Removing…" : "Remove access"}
              </button>
            </>
          }>
          <span className={"text-sm leading-relaxed text-[#3A4256]"}>
            {personName(removing)} won’t be able to sign in to{" "}
            {possessive(data?.clientName ?? "this client")} portal anymore.
            Files already uploaded stay on the client’s profile. You can invite
            them again later.
          </span>
        </PortalModal>
      )}
    </>
  );
};
